import React, { useState, useEffect, useCallback } from "react";
import {
  MdNoteAlt,
  MdNotes,
  MdLink,
  MdEmail,
  MdPushPin,
  MdSearch,
  MdClose,
  MdPerson,
  MdContentCopy,
  MdCheck,
  MdOpenInFull,
  MdDateRange,
  MdFilterAlt,
  MdPeople,
} from "react-icons/md";
import { formatDistanceToNow, format, isAfter, isBefore, startOfDay, endOfDay, subDays } from "date-fns";
import api from "../services/api";
import toast from "react-hot-toast";
import "./Notepad.css";
import "./AdminNotepad.css";

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:5000";

const AVATAR_COLORS = ["#6366f1","#f59e0b","#10b981","#ef4444","#3b82f6","#8b5cf6"];
const aColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
const initials = (name) =>
  name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

const TYPE_META = {
  note:  { label: "Note",  Icon: MdNotes,  color: "#f59e0b", bg: "rgba(245,158,11,0.10)"  },
  link:  { label: "Link",  Icon: MdLink,   color: "#6366f1", bg: "rgba(99,102,241,0.10)"  },
  email: { label: "Email", Icon: MdEmail,  color: "#10b981", bg: "rgba(16,185,129,0.10)"  },
};

const DATE_PRESETS = [
  { value: "all",    label: "All time"     },
  { value: "today",  label: "Today"        },
  { value: "week",   label: "Last 7 days"  },
  { value: "month",  label: "Last 30 days" },
  { value: "custom", label: "Custom range" },
];

const UserAvatar = ({ user, size = 38 }) => {
  const src = user?.avatar
    ? user.avatar.startsWith("http") ? user.avatar : `${API_URL}${user.avatar}`
    : null;
  return (
    <div
      className="avatar"
      style={{ width: size, height: size, fontSize: size * 0.32,
        background: src ? "transparent" : aColor(user?.name), flexShrink: 0 }}
    >
      {src
        ? <img src={src} alt={user?.name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
        : <span style={{ color: "#fff", fontWeight: 700 }}>{initials(user?.name)}</span>
      }
    </div>
  );
};

/* ── Read-only view modal ───────────────────────────────── */
const ViewModal = ({ note, onClose }) => {
  const [copied, setCopied] = useState(false);
  const { Icon, color, bg } = TYPE_META[note.type] || TYPE_META.note;
  const canCopy = note.type === "link" || note.type === "email";

  const handleCopy = () => {
    navigator.clipboard.writeText(note.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="np-modal-overlay" onClick={onClose}>
      <div
        className={`np-modal np-view-modal ${note.color ? `np-card--bg-${note.color}` : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="np-modal-header">
          <span className="np-card-type-badge" style={{ background: bg, color }}>
            <Icon size={13} />
            {TYPE_META[note.type]?.label}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {canCopy && (
              <button className="np-icon-btn" title="Copy content" onClick={handleCopy}>
                {copied ? <MdCheck size={18} color="#10b981" /> : <MdContentCopy size={18} />}
              </button>
            )}
            <button className="np-icon-btn" title="Close" onClick={onClose}>
              <MdClose size={20} />
            </button>
          </div>
        </div>
        <div className="np-view-body">
          {note.title && <h2 className="np-view-title">{note.title}</h2>}
          <div className={`np-view-content ${note.type === "link" ? "np-card-content--link" : ""}`}>
            {note.type === "link" ? (
              <a href={note.content} target="_blank" rel="noopener noreferrer">{note.content}</a>
            ) : note.content}
          </div>
          <div className="np-view-meta">
            {note.isPinned && <span className="np-pinned-label"><MdPushPin size={11} /> Pinned</span>}
            <span className="np-card-time">
              Last updated {format(new Date(note.updatedAt), "dd MMM yyyy, hh:mm a")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Read-only note card ────────────────────────────────── */
const ReadOnlyNoteCard = ({ note, onView }) => {
  const [copied, setCopied] = useState(false);
  const { Icon, color, bg } = TYPE_META[note.type] || TYPE_META.note;
  const canCopy = note.type === "link" || note.type === "email";

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(note.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className={`np-card ${note.isPinned ? "np-card--pinned" : ""} ${note.color ? `np-card--bg-${note.color}` : ""}`}
      onClick={() => onView(note)}
      style={{ cursor: "pointer" }}
    >
      <div className="np-card-header">
        <span className="np-card-type-badge" style={{ background: bg, color }}>
          <Icon size={13} />
          {TYPE_META[note.type]?.label}
        </span>
        <div className="np-card-actions" style={{ opacity: 1 }} onClick={(e) => e.stopPropagation()}>
          {canCopy && (
            <button className="np-icon-btn" title="Copy to clipboard" onClick={handleCopy}>
              {copied ? <MdCheck size={16} color="#10b981" /> : <MdContentCopy size={16} />}
            </button>
          )}
          <button className="np-icon-btn" title="View full note" onClick={(e) => { e.stopPropagation(); onView(note); }}>
            <MdOpenInFull size={15} />
          </button>
          {note.isPinned && <MdPushPin size={15} color="#6366f1" />}
        </div>
      </div>
      {note.title && <div className="np-card-title">{note.title}</div>}
      <div className={`np-card-content ${note.type === "link" ? "np-card-content--link" : ""}`}>
        {note.type === "link" ? (
          <a href={note.content} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            {note.content}
          </a>
        ) : note.content}
      </div>
      <div className="np-card-footer">
        {note.isPinned && <span className="np-pinned-label"><MdPushPin size={11} /> Pinned</span>}
        <span className="np-card-time">
          {formatDistanceToNow(new Date(note.updatedAt), { addSuffix: true })}
        </span>
      </div>
    </div>
  );
};

/* ── Main AdminNotepad page ─────────────────────────────── */
const AdminNotepad = () => {
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userNotes, setUserNotes]       = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [userSearch, setUserSearch]     = useState("");
  const [noteSearch, setNoteSearch]     = useState("");
  const [typeFilter, setTypeFilter]     = useState("all");
  const [datePreset, setDatePreset]     = useState("all");
  const [dateFrom, setDateFrom]         = useState("");
  const [dateTo, setDateTo]             = useState("");
  const [viewNote, setViewNote]         = useState(null);
  const [showDateFilter, setShowDateFilter] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/notepad/admin");
      setUsers(data.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const selectUser = async (u) => {
    setSelectedUser(u);
    setUserNotes([]);
    setTypeFilter("all");
    setNoteSearch("");
    setDatePreset("all");
    setDateFrom("");
    setDateTo("");
    setNotesLoading(true);
    try {
      const { data } = await api.get(`/notepad/admin/${u._id}`);
      setUserNotes(data.data);
    } catch {
      toast.error("Failed to load notes");
    } finally {
      setNotesLoading(false);
    }
  };

  /* ── Filtering logic ── */
  const applyDateFilter = (note) => {
    const updated = new Date(note.updatedAt);
    const now = new Date();
    if (datePreset === "today")  return isAfter(updated, startOfDay(now));
    if (datePreset === "week")   return isAfter(updated, startOfDay(subDays(now, 7)));
    if (datePreset === "month")  return isAfter(updated, startOfDay(subDays(now, 30)));
    if (datePreset === "custom") {
      if (dateFrom && isBefore(updated, startOfDay(new Date(dateFrom)))) return false;
      if (dateTo   && isAfter(updated,  endOfDay(new Date(dateTo))))     return false;
    }
    return true;
  };

  const filteredNotes = userNotes.filter((n) => {
    if (typeFilter !== "all" && n.type !== typeFilter) return false;
    if (!applyDateFilter(n)) return false;
    if (noteSearch) {
      const q = noteSearch.toLowerCase();
      if (!n.title?.toLowerCase().includes(q) && !n.content.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const filteredUsers = users.filter((u) => {
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const counts = { all: userNotes.length, note: 0, link: 0, email: 0 };
  userNotes.forEach((n) => { if (counts[n.type] !== undefined) counts[n.type]++; });

  const activeFilterCount =
    (typeFilter !== "all" ? 1 : 0) +
    (datePreset !== "all" ? 1 : 0) +
    (noteSearch ? 1 : 0);

  const clearAllFilters = () => {
    setTypeFilter("all");
    setDatePreset("all");
    setDateFrom("");
    setDateTo("");
    setNoteSearch("");
  };

  const roleLabel = (role) => {
    const map = { super_admin: "Super Admin", admin: "Admin", staff: "Staff", accounts: "Accounts", hr: "HR" };
    return map[role] || role;
  };

  return (
    <main className="page np-page anp-page">

      {/* ── Page header ── */}
      <div className="anp-page-header">
        <div className="anp-page-header-left">
          <div className="anp-header-icon-wrap">
            <MdNoteAlt size={26} />
          </div>
          <div>
            <h1 className="np-header-title">Team Notepads</h1>
            <p className="np-header-sub">Browse and read all team members' personal notepads</p>
          </div>
        </div>
        <div className="anp-page-stats">
          <div className="anp-stat-pill">
            <MdPeople size={15} />
            <span>{users.length} members</span>
          </div>
          <div className="anp-stat-pill">
            <MdNoteAlt size={15} />
            <span>{users.reduce((a, u) => a + (u.noteCount || 0), 0)} total notes</span>
          </div>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div className="anp-layout">

        {/* Left — user list */}
        <div className="anp-users-panel">
          <div className="anp-panel-header">
            <div className="anp-panel-header-left">
              <span className="anp-panel-title">Team Members</span>
              <span className="anp-panel-count">{filteredUsers.length}</span>
            </div>
          </div>

          <div className="anp-user-search-wrap">
            <MdSearch size={15} className="anp-search-icon" />
            <input
              className="anp-user-search-input"
              placeholder="Search members…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            {userSearch && (
              <button className="np-icon-btn" style={{ position: "absolute", right: 8 }} onClick={() => setUserSearch("")}>
                <MdClose size={13} />
              </button>
            )}
          </div>

          {loading ? (
            <div className="loading" style={{ padding: "40px 0" }}><div className="spinner" /></div>
          ) : filteredUsers.length === 0 ? (
            <div className="anp-no-users">No members found</div>
          ) : (
            <ul className="anp-user-list">
              {filteredUsers.map((u) => (
                <li
                  key={u._id}
                  className={`anp-user-item ${selectedUser?._id === u._id ? "active" : ""}`}
                  onClick={() => selectUser(u)}
                >
                  <div className="anp-user-avatar-wrap">
                    <UserAvatar user={u} size={36} />
                    {u.noteCount > 0 && <span className="anp-avatar-badge">{u.noteCount}</span>}
                  </div>
                  <div className="anp-user-info">
                    <span className="anp-user-name">{u.name}</span>
                    <span className="anp-user-role-badge" data-role={u.role}>
                      {roleLabel(u.role)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right — notes panel */}
        <div className="anp-notes-panel">
          {!selectedUser ? (
            <div className="anp-select-prompt">
              <div className="anp-prompt-icon"><MdPerson size={40} /></div>
              <h3>Select a team member</h3>
              <p>Choose a member from the list to browse their notepad</p>
            </div>
          ) : (
            <>
              {/* User context bar */}
              <div className="anp-user-context-bar">
                <UserAvatar user={selectedUser} size={46} />
                <div className="anp-context-info">
                  <div className="anp-context-name">{selectedUser.name}</div>
                  {selectedUser.designation && (
                    <div className="anp-context-line">{selectedUser.designation}</div>
                  )}
                  <div className="anp-context-line">{selectedUser.email}</div>
                </div>
                <div className="anp-context-counters">
                  {Object.entries({ note: "Notes", link: "Links", email: "Emails" }).map(([k, label]) => (
                    <div key={k} className="anp-counter-item">
                      <span className="anp-counter-val">{counts[k]}</span>
                      <span className="anp-counter-label">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Filter bar */}
              <div className="anp-filter-bar">
                {/* Type pills */}
                <div className="np-type-filter">
                  {["all", "note", "link", "email"].map((t) => (
                    <button
                      key={t}
                      className={`np-filter-tab ${typeFilter === t ? "active" : ""}`}
                      onClick={() => setTypeFilter(t)}
                    >
                      {t === "all" ? "All" : TYPE_META[t].label}
                      <span className="np-filter-count">{t === "all" ? filteredNotes.length : counts[t]}</span>
                    </button>
                  ))}
                </div>

                <div className="anp-filter-right">
                  {/* Search */}
                  <div className="np-search-wrap" style={{ minWidth: 180 }}>
                    <MdSearch size={16} className="np-search-icon" />
                    <input
                      className="np-search"
                      placeholder="Search notes…"
                      value={noteSearch}
                      onChange={(e) => setNoteSearch(e.target.value)}
                    />
                    {noteSearch && (
                      <button className="np-search-clear" onClick={() => setNoteSearch("")}>
                        <MdClose size={13} />
                      </button>
                    )}
                  </div>

                  {/* Date filter toggle */}
                  <button
                    className={`anp-date-btn ${showDateFilter || datePreset !== "all" ? "active" : ""}`}
                    onClick={() => setShowDateFilter((v) => !v)}
                    title="Date filter"
                  >
                    <MdDateRange size={16} />
                    {datePreset !== "all"
                      ? DATE_PRESETS.find((p) => p.value === datePreset)?.label
                      : "Date"}
                    {datePreset !== "all" && (
                      <span
                        className="anp-clear-date"
                        onClick={(e) => { e.stopPropagation(); setDatePreset("all"); setDateFrom(""); setDateTo(""); }}
                      >
                        <MdClose size={12} />
                      </span>
                    )}
                  </button>

                  {/* Active filter count */}
                  {activeFilterCount > 0 && (
                    <button className="anp-clear-all-btn" onClick={clearAllFilters}>
                      <MdFilterAlt size={14} />
                      Clear ({activeFilterCount})
                    </button>
                  )}
                </div>
              </div>

              {/* Date filter dropdown */}
              {showDateFilter && (
                <div className="anp-date-panel">
                  <div className="anp-date-presets">
                    {DATE_PRESETS.filter((p) => p.value !== "custom").map((p) => (
                      <button
                        key={p.value}
                        className={`anp-preset-btn ${datePreset === p.value ? "active" : ""}`}
                        onClick={() => { setDatePreset(p.value); if (p.value !== "custom") { setDateFrom(""); setDateTo(""); } }}
                      >
                        {p.label}
                      </button>
                    ))}
                    <button
                      className={`anp-preset-btn ${datePreset === "custom" ? "active" : ""}`}
                      onClick={() => setDatePreset("custom")}
                    >
                      Custom range
                    </button>
                  </div>
                  {datePreset === "custom" && (
                    <div className="anp-custom-range">
                      <label className="anp-date-label">
                        From
                        <input type="date" className="anp-date-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                      </label>
                      <span className="anp-range-sep">→</span>
                      <label className="anp-date-label">
                        To
                        <input type="date" className="anp-date-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Notes grid */}
              {notesLoading ? (
                <div className="loading" style={{ padding: "60px 0" }}>
                  <div className="spinner" /><span className="loading-text">Loading notes…</span>
                </div>
              ) : filteredNotes.length === 0 ? (
                <div className="np-empty" style={{ paddingTop: 60 }}>
                  <MdNoteAlt size={48} className="np-empty-icon" />
                  <h3>{userNotes.length === 0 ? "No notes yet" : "No notes match your filters"}</h3>
                  <p>
                    {userNotes.length === 0
                      ? `${selectedUser.name} hasn't saved any notes yet.`
                      : "Try adjusting the search, type, or date filters."}
                  </p>
                  {userNotes.length > 0 && (
                    <button className="btn btn-secondary" onClick={clearAllFilters}>Clear all filters</button>
                  )}
                </div>
              ) : (
                <div className="np-grid">
                  {filteredNotes.map((note) => (
                    <ReadOnlyNoteCard key={note._id} note={note} onView={setViewNote} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {viewNote && <ViewModal note={viewNote} onClose={() => setViewNote(null)} />}
    </main>
  );
};

export default AdminNotepad;
